import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ColumnDef, FilterFn } from '@tanstack/react-table';

import useFundContext from '@/contexts/Fund/useFundContext';
import { SupplierFullDtoIn } from '@/models/in/SupplierFullDtoIn';
import { SupplierApprovalStatus } from '@/models/common/ProjectStatus';
import { getSupplierApprovalStatusColor } from '@/helpers/projectStatus';
import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import ApprovalStatusTabs from './ApprovalStatusTabs';
import { ApprovalTab, countByTab, fundSupplierStatus, supplierApprovalTab } from './approvalTabs';

const domainOf = (supplier: SupplierFullDtoIn): string =>
  supplier.activityDomainName || supplier.activityDomainOther || '';

const supplierSearch: FilterFn<SupplierFullDtoIn> = (row, _columnId, value) => {
  if (!value) return true;
  const searchValue = String(value).toLowerCase();
  const supplier = row.original;
  return [supplier.name, supplier.taxId, domainOf(supplier), ...(supplier.projectNames ?? [])].some((field) =>
    field?.toLowerCase().includes(searchValue)
  );
};

/**
 * "ספקים" - the fund's suppliers, approved and not, as the second sub-tab under "מיזמים"
 * (HAT-1105, 22/10/2026: "רשימת מיזמים מאושרים ושלא מאושרים וכן רשימת ספקים לקרן, כמו שמופיע
 * בבקשות תמיכה").
 *
 * A fund's suppliers are the ones a request was paid to and the ones named on its projects, so a
 * supplier a coordinator has just added is here at once - marked "ממתין לאישור" until the office
 * has looked at it, instead of being nowhere to be found.
 */
const ProjectSuppliersPage = () => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const navigate = useNavigate();
  const { fundId: routeFundId } = useParams();

  const [searchQuery, setSearchQuery] = useState('');
  const [tab, setTab] = useState<ApprovalTab>('all');

  const { data: suppliers, isLoading } = useGetAuthApi<SupplierFullDtoIn[]>(
    currentFund?.id ? `funds/${currentFund.id}/suppliers` : ''
  );

  const allSuppliers = useMemo(() => suppliers ?? [], [suppliers]);
  // SPR-10302: the office approves a supplier per project, so its status here comes from its
  // projects in this fund - see fundSupplierStatus.
  const tabCounts = useMemo(
    () => countByTab(allSuppliers, (supplier) => supplierApprovalTab(fundSupplierStatus(supplier))),
    [allSuppliers]
  );
  const visibleSuppliers = useMemo(
    () => (tab === 'all' ? allSuppliers : allSuppliers.filter((supplier) => supplierApprovalTab(fundSupplierStatus(supplier)) === tab)),
    [allSuppliers, tab]
  );

  const columns: ColumnDef<SupplierFullDtoIn>[] = [
    {
      id: 'name',
      header: Col(t('projects.suppliers.columns.name')),
      accessorKey: 'name',
      enableSorting: true,
      meta: { className: 'min-w-[200px]' },
    },
    {
      id: 'taxId',
      header: Col(t('projects.suppliers.columns.taxId')),
      accessorKey: 'taxId',
      enableSorting: true,
      meta: { className: 'min-w-[130px]' },
    },
    {
      id: 'activityDomain',
      header: Col(t('projects.suppliers.columns.activityDomain')),
      enableSorting: false,
      cell: ({ row }) => <span>{domainOf(row.original) || '—'}</span>,
      meta: { className: 'min-w-[150px]' },
    },
    {
      id: 'projects',
      header: Col(t('projects.suppliers.columns.projects')),
      enableSorting: false,
      // Each project with the supplier's approval for it (SPR-10302). An em dash for a supplier no
      // project of this fund names (one only requests were paid to).
      cell: ({ row }) => {
        const approvals = row.original.projectApprovals ?? [];
        if (approvals.length === 0) {
          return <span>{(row.original.projectNames ?? []).join(', ') || '—'}</span>;
        }
        return (
          <div className="flex flex-col gap-1">
            {approvals.map((approval) => (
              <div key={approval.projectId} className="flex items-center gap-2">
                <div
                  className="w-[8px] h-[8px] rounded-full shrink-0"
                  style={{ backgroundColor: getSupplierApprovalStatusColor(approval.approvalStatus) }}
                />
                <span>
                  {approval.projectName} ({t(`projects.suppliers.status.${approval.approvalStatus}`)})
                </span>
              </div>
            ))}
          </div>
        );
      },
      meta: { className: 'min-w-[200px]' },
    },
    {
      id: 'status',
      header: Col(t('projects.suppliers.columns.status')),
      accessorFn: (supplier) => fundSupplierStatus(supplier),
      enableSorting: true,
      cell: ({ row }) => {
        // A server that sent no status is read as waiting, the same way the tabs read it.
        const status = fundSupplierStatus(row.original) ?? SupplierApprovalStatus.PendingApproval;
        return (
          <div className="flex items-center gap-2">
            <div
              className="w-[10px] h-[10px] rounded-full shrink-0"
              style={{ backgroundColor: getSupplierApprovalStatusColor(status) }}
            />
            <span>{t(`projects.suppliers.status.${status}`)}</span>
          </div>
        );
      },
      meta: { className: 'min-w-[150px]' },
    },
    {
      id: 'createdAt',
      header: Col(t('projects.suppliers.columns.createdAt')),
      accessorKey: 'createdAt',
      enableSorting: true,
      cell: ({ row }) => (
        <span>
          {Intl.DateTimeFormat('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' })
            .format(new Date(row.original.createdAt))}
        </span>
      ),
      meta: { className: 'min-w-[120px]' },
    },
  ];

  return (
    <div>
      <ApprovalStatusTabs value={tab} onChange={setTab} counts={tabCounts} />

      <DataTable
        title={t('projects.suppliers.titleWithCount', { count: visibleSuppliers.length })}
        data={visibleSuppliers}
        isLoading={isLoading}
        columns={columns}
        filters={
          <div className="flex items-center justify-between gap-3">
            <Button variant="primary" onClick={() => navigate(`/${routeFundId}/projects/new-supplier`)}>
              {t('projects.sidebar.newSupplier')}
            </Button>
            <SearchInput value={searchQuery} onChange={setSearchQuery} placeholder="projects.suppliers.search" />
          </div>
        }
        emptyStateContent={
          tab === 'all' || allSuppliers.length === 0
            ? { title: t('projects.suppliers.noData'), description: t('projects.suppliers.noDataDescription') }
            : { title: t('projects.suppliers.noDataInTab'), description: t('projects.suppliers.noDataInTabDescription') }
        }
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customGlobalSearch={supplierSearch}
      />
    </div>
  );
};

export default ProjectSuppliersPage;
